<script lang="ts">
export default {
  name: 'SpreadsheetUploadPanel',
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { X, Check, FileSpreadsheet } from '@lucide/vue';
import { TransformWorksheetToPolicies, UNREADABLE_FILE_ERROR } from '@/utilities/ExternalPolicyUploadTransform';
import { acceptContentTowerTypes } from '@/utilities/tower/Upload';
import { ExternalPolicy } from '@/types/PolicyCBO';
import { accountService, parentAccountService } from '@/services/api';
import { Account, ParentAccountCompany } from '@/types/Account';
import { documentService } from '@/services/v2/DocumentService.js';
import AppSelect from '@/components/v2/AppSelect.vue';
import Spinner from '@/components/v2/Spinner.vue';

const props = defineProps<{
  accountId: string;
  isParent?: boolean;
}>();

const emit = defineEmits<{
  uploaded: [];
}>();

enum Step {
  Uploading,
  Validating,
}

const binaryData = ref<ArrayBuffer | null>(null);
const currentFile = ref<File>();
const currentErrors = ref<string[]>([]);
const currentPolicies = ref<ExternalPolicy[]>([]);
const isValidating = ref<boolean>(false);
const isUploading = ref<boolean>(false);
const uploadErrors = ref<string[]>([]);
const isValid = ref<boolean>(false);
const fileInput = ref<HTMLInputElement | null>(null);
const step = ref<Step>(Step.Uploading);

// Parent account reconciliation
const account = ref<Account>();
const parentCompanies = ref<ParentAccountCompany[]>([]);
const policiesToCorrect = ref<ExternalPolicy[]>([]);

const actionButtonLabel = computed(() => {
  if (!props.isParent) {
    return 'Upload';
  }
  return step.value === Step.Validating ? 'Upload' : 'Validate';
});

const policyAccountsValid = computed(() => {
  return policiesToCorrect.value.every((policy) => policy.accountId);
});

const actionDisabled = computed(() => {
  if (!props.isParent) {
    return !(currentFile.value && isValid.value);
  }
  return !(
    currentFile.value &&
    isValid.value &&
    (step.value === Step.Validating ? policyAccountsValid.value : true)
  );
});

function handleAction() {
  if (props.isParent && step.value === Step.Uploading) {
    checkChildAccounts();
    step.value = Step.Validating;
  } else {
    uploadPolicies();
  }
}

async function handleDownloadTemplate() {
  const target = await documentService.downloadPolicyTemplate();

  const anchorElement = document.createElement('a');
  anchorElement.href = target;
  anchorElement.download = 'external_policy_template';
  anchorElement.setAttribute('target', '_blank');
  anchorElement.click();
  anchorElement.remove();
}

function checkChildAccounts() {
  for (let policy of currentPolicies.value) {
    const company = parentCompanies.value.find(
      (company) => company.accountName?.trim().toLowerCase() === policy.accountName?.trim().toLowerCase(),
    );
    if (company) {
      policy.accountId = company.accountId;
    } else if (policy.accountName?.trim().toLowerCase() === account.value?.accountName?.trim().toLowerCase()) {
      // when uploading policy tied to the parent account
      policy.accountId = account.value.accountID;
    }
  }

  policiesToCorrect.value = currentPolicies.value.filter((policy) => !policy.accountId);
  currentPolicies.value = currentPolicies.value.filter((policy) => policy.accountId);
}

async function uploadPolicies() {
  if (!isValid.value) {
    return false;
  }

  isUploading.value = true;

  let validPolicy: boolean;
  let messages: string[];

  try {
    if (props.isParent) {
      const response = await parentAccountService.uploadExternalPolicies(props.accountId, [
        ...currentPolicies.value,
        ...policiesToCorrect.value,
      ]);
      validPolicy = response.validPolicy;
      messages = response.messages;
    } else {
      const response = await accountService.uploadPolicies(props.accountId, currentPolicies.value);
      validPolicy = response.validPolicy;
      messages = response.messages;
    }
  } catch (err) {
    console.error(err);
    validPolicy = false;
    // A 409 means the upload lost a lock contest against a concurrent write and was rolled
    // back cleanly. Re-submitting the same file works, so say so rather than showing the
    // generic failure that gave the user no reason to try again.
    const status = (err as { cause?: { status?: number } })?.cause?.status;
    messages =
      status === 409
        ? ['The system was busy processing another change. Please try uploading again.']
        : ['An error occurred while uploading policies'];
  }

  isUploading.value = false;
  isValid.value = validPolicy;
  uploadErrors.value = messages || [];
  if (!validPolicy) {
    return false;
  }

  emit('uploaded');
}

function readFile(event: Event) {
  if (!event || !event.target) {
    return;
  }
  const filesToRead = (event.target as HTMLInputElement).files;
  if (!filesToRead || filesToRead.length === 0) {
    return;
  }

  const file = filesToRead[0];
  const reader = new FileReader();
  reader.onload = () => {
    binaryData.value = reader.result as ArrayBuffer;
    currentFile.value = file;
    isValidating.value = true;
    // The try/catch has to live inside the callback: it runs on a later task, so a throw
    // here would escape an outer catch, leave isValidating stuck true, and lock the panel.
    setTimeout(() => {
      try {
        const { policies, errors } = TransformWorksheetToPolicies(binaryData.value);
        isValid.value = errors.length === 0;
        currentErrors.value = errors;
        currentPolicies.value = policies;
      } catch (err) {
        console.error('Failed to read the uploaded workbook:', err);
        isValid.value = false;
        currentErrors.value = [UNREADABLE_FILE_ERROR];
        currentPolicies.value = [];
      } finally {
        isValidating.value = false;
      }
    }, 200);
  };
  reader.readAsArrayBuffer(file);
}

function resetFile() {
  if (!isValidating.value && !isUploading.value) {
    binaryData.value = null;
    currentFile.value = undefined;
    currentErrors.value = [];
    currentPolicies.value = [];
    isValid.value = false;
    uploadErrors.value = [];
    step.value = Step.Uploading;
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
}

onMounted(async () => {
  if (props.isParent) {
    account.value = await accountService.findById(props.accountId);
    parentCompanies.value = await parentAccountService.companies(props.accountId, { external: 'true' });
  }
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <p class="text-sm text-base-content/60 italic" data-testid="spreadsheet-panel-description">
      {{
        step === Step.Validating
          ? policyAccountsValid
            ? ''
            : 'Client names were not found for the following policies. Please correct these names before uploading.'
          : 'Upload policies external to CAC business. Please upload CAC-placed policies in Epic.'
      }}
    </p>

    <div v-if="step === Step.Uploading" class="flex flex-col gap-6">
      <button
        class="flex w-fit items-center gap-1 duration-[75ms] hover:text-secondary"
        data-testid="download-template"
        @click="handleDownloadTemplate">
        <FileSpreadsheet :size="18" class="text-secondary" />
        <span class="text-sm">Download Excel Template</span>
      </button>

      <div class="relative flex w-full flex-col">
        <input
          class="file-input-bordered file-input w-full border border-solid file-input-sm file-input-secondary"
          :class="{ 'border-error': currentErrors.length > 0 }"
          ref="fileInput"
          type="file"
          data-testid="spreadsheet-file-input"
          @change="readFile"
          :accept="acceptContentTowerTypes" />
        <X
          v-if="currentFile"
          @click="resetFile"
          class="absolute top-1 right-1 cursor-pointer text-gray-300 hover:text-gray-500" />

        <div class="policy-validation-errors mt-1 p-2" v-if="currentErrors.length > 0 && !isValidating">
          <span class="text-error">Cannot upload due to the following:</span>
          <ul>
            <li v-for="(error, index) of currentErrors" :key="index">{{ error }}</li>
          </ul>
        </div>

        <div class="policy-upload-errors mt-1 p-2" v-if="uploadErrors.length > 0 && !isValidating">
          <span class="text-error">Upload failed due to the following:</span>
          <ul>
            <li v-for="(error, index) of uploadErrors" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-else class="flex max-h-[60vh] flex-col gap-6 overflow-y-auto">
      <table v-if="policiesToCorrect && policiesToCorrect.length > 0" class="table-pin-rows table table-xs">
        <thead>
          <tr>
            <th>Account Name</th>
            <th>Description</th>
            <th>Carrier</th>
            <th>Policy Number</th>
            <th>Corrected Account Name</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="policy in policiesToCorrect" :key="policy.id">
            <td class="max-w-32 text-secondary">{{ policy.accountName }}</td>
            <td class="max-w-56">{{ policy.description }}</td>
            <td>{{ policy.insurerGroup }}</td>
            <td class="max-w-44">{{ policy.mainPolicyNumber }}</td>
            <td class="min-w-40">
              <AppSelect
                v-model="policy.accountId"
                :options="
                  parentCompanies.map((company) => ({ value: company.accountId, label: company.accountName || '' }))
                "
                class="h-9 w-full"
                placeholder="Select account" />
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="flex items-center gap-2 py-4">
        <Check :size="16" class="text-green-600" />
        <span>All Policies are valid, click upload to continue</span>
      </div>

      <div class="p-2" v-if="uploadErrors.length > 0 && !isValidating">
        <span class="text-error">Upload failed:</span>
        <ul>
          <li v-for="(error, index) of uploadErrors" :key="index">{{ error }}</li>
        </ul>
      </div>
    </div>

    <div class="flex items-center justify-end gap-2">
      <Spinner v-if="isValidating || isUploading" />
      <button
        class="btn btn-sm btn-primary"
        data-testid="spreadsheet-action"
        :disabled="actionDisabled || isValidating || isUploading"
        @click="handleAction">
        {{ actionButtonLabel }}
      </button>
    </div>
  </div>
</template>
